import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

/**
 * Fullscreen image lightbox.
 *
 * Props:
 *   images: string[]        — image URLs in display order
 *   open: boolean
 *   index: number           — currently displayed image
 *   onClose: () => void
 *   onIndexChange: (n) => void  — caller manages index state so we can
 *                                 share it with the thumbnail strip
 *
 * Keyboard: ←/→ to navigate, Esc to close. Click backdrop to close.
 */
const GalleryLightbox = ({ images = [], open, index = 0, onClose, onIndexChange }) => {
  const total = images.length;
  const safeIndex = total > 0 ? ((index % total) + total) % total : 0;

  const goPrev = useCallback(
    () => onIndexChange?.((safeIndex - 1 + total) % total),
    [onIndexChange, safeIndex, total]
  );
  const goNext = useCallback(
    () => onIndexChange?.((safeIndex + 1) % total),
    [onIndexChange, safeIndex, total]
  );

  // Keyboard handlers + body-scroll lock while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft' && total > 1) goPrev();
      else if (e.key === 'ArrowRight' && total > 1) goNext();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, total, onClose, goPrev, goNext]);

  if (!open || total === 0) return null;

  const current = images[safeIndex];

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Gallery viewer"
    >
      {/* Close */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
        aria-label="Close gallery"
        data-testid="gallery-lightbox-close"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Counter */}
      <div
        className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/40 text-white text-sm tabular-nums"
        data-testid="gallery-lightbox-counter"
      >
        {safeIndex + 1} / {total}
      </div>

      {/* Prev */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-4 sm:left-8 w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
          aria-label="Previous image"
          data-testid="gallery-lightbox-prev"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}

      {/* Image */}
      <img
        src={current}
        alt={`Gallery ${safeIndex + 1} of ${total}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg shadow-2xl select-none"
        data-testid="gallery-lightbox-image"
        draggable={false}
      />

      {/* Next */}
      {total > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-4 sm:right-8 w-12 h-12 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors"
          aria-label="Next image"
          data-testid="gallery-lightbox-next"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}

      {/* Thumbnail strip (only when >1 image) */}
      {total > 1 && (
        <div
          className="absolute bottom-4 left-0 right-0 px-4 overflow-x-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-2 justify-center min-w-min">
            {images.map((src, i) => (
              <button
                key={src + i}
                type="button"
                onClick={() => onIndexChange?.(i)}
                className={`h-14 w-14 sm:h-16 sm:w-16 rounded-md overflow-hidden border-2 transition-all ${
                  i === safeIndex
                    ? 'border-primary scale-110'
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
                aria-label={`Go to image ${i + 1}`}
                data-testid={`gallery-lightbox-thumb-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" draggable={false} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default GalleryLightbox;
